import type { PlayerCharacter } from "../types/entity.js";

/**
 * Party-wide sneak toggle + noise-radius math.
 *
 * Per `specs/enemies-and-ai.md §Sneak mechanic`:
 *  - Toggle is party-wide, not per-character.
 *  - While active: party movement is halved (`SNEAK_SPEED_MULTIPLIER`)
 *    and every party-emitted noise radius is reduced.
 *  - Base reduction is ×0.5. High-DEX front-row characters contribute
 *    further — in v1 we gate on `DEX ≥ 14` and average contributions
 *    across the front row. Floor is ×0.1 (sneak still leaks some noise).
 *
 * The state lives in a module singleton so both the input handler and
 * any future HUD indicator can read/write it without prop-drilling.
 */

// ── Constants ────────────────────────────────────────────────────────────────

/** Multiplier applied to the base noise radius when sneaking is active. */
export const SNEAK_BASE_MULTIPLIER = 0.5;
/** Extra per-character reduction for each high-DEX / Stealth-proficient front-row member. */
export const SNEAK_FRONT_ROW_CONTRIBUTION = 0.1;
/** Minimum radius multiplier — sneak can't reduce noise below this. */
export const SNEAK_MIN_MULTIPLIER = 0.1;
/** DEX threshold that flags a character as stealthy for the front-row bonus. */
export const SNEAK_DEX_THRESHOLD = 14;
/** Movement-tween multiplier applied while sneak is active (2.0 = half speed). */
export const SNEAK_SPEED_MULTIPLIER = 2.0;

// ── Module singleton ─────────────────────────────────────────────────────────

type Listener = (sneaking: boolean) => void;

let sneaking = false;
let listeners: Listener[] = [];

export const SneakState = {
  /** Current sneak flag. */
  isSneaking(): boolean {
    return sneaking;
  },

  /**
   * Set the sneak flag explicitly. No-op when the flag is already in the
   * requested state (no listener dispatch).
   */
  set(next: boolean): void {
    if (next === sneaking) return;
    sneaking = next;
    for (const fn of listeners) fn(sneaking);
  },

  /** Flip the flag. Returns the new state. */
  toggle(): boolean {
    this.set(!sneaking);
    return sneaking;
  },

  /**
   * Subscribe to flag changes. Returns an unsubscribe handle. Listeners
   * are invoked synchronously in registration order.
   */
  subscribe(fn: Listener): () => void {
    listeners.push(fn);
    return () => {
      listeners = listeners.filter((x) => x !== fn);
    };
  },

  /** Test-only — reset state and listeners. */
  reset(): void {
    sneaking = false;
    listeners = [];
  },
};

// ── Radius math ──────────────────────────────────────────────────────────────

/**
 * Compute the noise-radius multiplier to apply to party-emitted noise.
 * When `sneaking` is false the multiplier is 1.0 (no change).
 */
export function sneakRadiusMultiplier(
  sneakingNow: boolean,
  frontRow: readonly PlayerCharacter[],
): number {
  if (!sneakingNow) return 1;
  if (frontRow.length === 0) return SNEAK_BASE_MULTIPLIER;

  const contribs: number[] = frontRow.map((c) =>
    c.abilities.DEX >= SNEAK_DEX_THRESHOLD ? SNEAK_FRONT_ROW_CONTRIBUTION : 0,
  );
  const avg = contribs.reduce((a, b) => a + b, 0) / contribs.length;
  return Math.max(SNEAK_MIN_MULTIPLIER, SNEAK_BASE_MULTIPLIER - avg);
}

/**
 * Apply the full sneak modifier stack to a raw noise radius. The result
 * is rounded down to integer cells since `NoiseBroadcast` compares with
 * Manhattan distance — fractional radii just waste CPU.
 */
export function applySneakToRadius(
  baseRadius: number,
  sneakingNow: boolean,
  frontRow: readonly PlayerCharacter[],
): number {
  const mult = sneakRadiusMultiplier(sneakingNow, frontRow);
  return Math.max(0, Math.floor(baseRadius * mult));
}
